import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Case,
  CaseClientStatus,
  CaseExport,
  CaseReviewStatus,
  CaseReviewView,
  CategoryFindingSummary,
  DocumentIntake,
  DocumentSegment,
  DocumentStatus,
  KeyedCaseCalcView,
  KeyValue,
  LensCaseFindings,
  NamedFirm,
  OverallOutcomeStatus,
  ReprocessType,
  SectionValueView,
  SegmentMetrics,
} from "generated/graphql";
import { USER_LOGOUT } from "redux/system.action";
import RequestStatus from "redux/slices/RequestStatus";
import {
  createCaseChronologyExport,
  createCaseExport,
  createCaseFullRecordSetExport,
  deleteCaseExport,
  deleteIntake,
  fetchBaseFindings,
  fetchCase,
  fetchCaseExports,
  fetchCaseHome,
  fetchCaseIntakes,
  fetchCaseSectionValues,
  fetchCaseSilent,
  fetchFilteredFindings,
  fetchIntake,
  fetchPossibleChangesFromAutoScripts,
  fetchSegment,
  reintakeDocument,
  renameIntake,
  setCollaboratorFirms,
  setDocumentsReviewed,
  setPlaintiffFirm,
  updateCaseClaimantInfo,
  updateCaseClientCaseId,
  updateCaseClientStatus,
  updateCaseExportCollectSharedState,
  updateCaseSummary,
  updateIntakeCustomProperties,
  updateSectionValueOutcome,
  updateSectionValueOutcomeAssignee,
  updateSectionValueOutcomeComment,
  updateSectionValueOutcomeStatus,
  updateSectionValuesReviewState,
  updateSectionValueValidation,
  updateSectionValueValidationAssignee,
  updateSectionValueValidationComment,
  updateSectionValueValidationStatus,
} from "./case.action";
import { KeyedCaseValueCalc, SegmentStatusUpdateEvent } from "Backend";
import { formatISO } from "date-fns";
import type { RootState } from "redux/store";

export type DocumentSegmentStaged = DocumentSegment & {
  processingDetails?: SegmentStatusUpdateEvent["processingDetails"];
};

export type DocumentIntakeAugmented = Omit<DocumentIntake, "documentSegments"> & {
  documentSegments: DocumentSegmentStaged[];
};

export interface CaseState {
  status: RequestStatus;
  case: Case;
  sectionValues: SectionValueView[];
  sectionValuesStatus: RequestStatus;
  calcs: KeyedCaseValueCalc[];
  caseInsightsPending: boolean;
  reviews: CaseReviewView[];
  intakesStatus: RequestStatus;
  intakes: DocumentIntakeAugmented[];
  exportsStatus: RequestStatus;
  exports: CaseExport[];
  deselectedExportTypes: string[];
  activeCategory?: CategoryFindingSummary;
  baseFindings?: LensCaseFindings;
  baseFindingsStatus: RequestStatus;
  filteredFindings?: LensCaseFindings;
  filteredFindingsStatus: RequestStatus;
  collaboratorFirms: NamedFirm[];
}

function upsertSectionValue(state, action) {
  const sectionValue = action.payload;
  const sectionValueIndex = state.sectionValues.findIndex((sv) => sv.sectionId === sectionValue.sectionId);

  if (sectionValueIndex !== -1) {
    state.sectionValues[sectionValueIndex] = sectionValue;
  } else {
    state.sectionValues.push(sectionValue);
  }
}

function valueCalcs(calcs: KeyedCaseCalcView[]): KeyedCaseValueCalc[] {
  return calcs.map((c) => ({
    ...c,
    timestamp: c.timestamp || formatISO(new Date()),
    value: c.value || "",
  }));
}

const EMPTY_CASE: Case = {
  id: -1,
  lensId: -1,
  documentCount: 0,
  pageCount: 0,
  info: {
    clientCaseId: "-1",
    claimantGivenName: "",
    claimantSurname: "",
  },
  reviewStatus: CaseReviewStatus.NoDocuments,
  clientStatus: CaseClientStatus.Inactive,
  propertiesArray: [],
  properties: [],
  createdAt: "",
  modifiedAt: "",
  sectionValues: [],
};

const initialState: CaseState = {
  case: EMPTY_CASE,
  status: RequestStatus.IDLE,
  sectionValues: [],
  sectionValuesStatus: RequestStatus.IDLE,
  calcs: [],
  caseInsightsPending: false,
  reviews: [],
  intakes: [],
  intakesStatus: RequestStatus.IDLE,
  exportsStatus: RequestStatus.IDLE,
  exports: [],
  deselectedExportTypes: [],
  activeCategory: undefined,
  baseFindings: undefined,
  baseFindingsStatus: RequestStatus.IDLE,
  filteredFindings: undefined,
  filteredFindingsStatus: RequestStatus.IDLE,
  collaboratorFirms: [],
};

const exportReducers = {
  deselectExportType: (state, action: PayloadAction<string>) => {
    state.deselectedExportTypes.push(action.payload);
  },
  selectExportType: (state, action: PayloadAction<string>) => {
    state.deselectedExportTypes.splice(
      state.deselectedExportTypes.findIndex((et) => et === action.payload),
      1
    );
  },
  updateExport: (state, action: PayloadAction<CaseExport>) => {
    const foundIndex = state.exports.findIndex((e) => e.id === action.payload.id);

    if (foundIndex > -1) {
      state.exports.splice(foundIndex, 1, action.payload);
    } else {
      state.exports.unshift(action.payload);
    }
  },
};
const exportExtraReducers = (builder: ActionReducerMapBuilder<CaseState>): void => {
  builder
    .addCase(fetchCaseExports.pending, (state) => {
      state.exportsStatus = RequestStatus.PENDING;
    })
    .addCase(fetchCaseExports.fulfilled, (state, action) => {
      state.exportsStatus = RequestStatus.SUCCEEDED;
      state.exports = action.payload.case.caseExports;
    })
    .addCase(fetchCaseExports.rejected, (state) => {
      state.exportsStatus = RequestStatus.FAILED;
    })
    .addCase(createCaseExport.fulfilled, (state, action) => {
      state.exports.unshift(action.payload);
    })
    .addCase(createCaseFullRecordSetExport.fulfilled, (state, action) => {
      state.exports.unshift(action.payload);
    })
    .addCase(createCaseChronologyExport.fulfilled, (state, action) => {
      state.exports.unshift(action.payload);
    })
    .addCase(deleteCaseExport.fulfilled, (state, action) => {
      const index = state.exports.findIndex((e) => e.id === action.payload.caseExportId);
      if (index !== -1) {
        state.exports.splice(index, 1);
      }
    })
    .addCase(updateCaseExportCollectSharedState.fulfilled, (state, action) => {
      const found = state.exports.find((e) => e.id === action.payload.caseExportId);
      if (found) {
        found.collectShared = action.payload.share;
      }
    });
};
const reviewExtraReducers = (builder: ActionReducerMapBuilder<CaseState>): void => {
  builder
    .addCase(fetchBaseFindings.pending, (state) => {
      state.baseFindingsStatus = RequestStatus.PENDING;
    })
    .addCase(fetchBaseFindings.rejected, (state) => {
      state.baseFindingsStatus = RequestStatus.FAILED;
    })
    .addCase(fetchBaseFindings.fulfilled, (state, action) => {
      state.baseFindings = action.payload.case.findings;
      state.baseFindingsStatus = RequestStatus.SUCCEEDED;
    })
    .addCase(fetchFilteredFindings.pending, (state) => {
      state.filteredFindingsStatus = RequestStatus.PENDING;
    })
    .addCase(fetchFilteredFindings.rejected, (state) => {
      state.filteredFindingsStatus = RequestStatus.FAILED;
    })
    .addCase(fetchFilteredFindings.fulfilled, (state, action) => {
      state.filteredFindings = action.payload.case.findings;
      state.filteredFindingsStatus = RequestStatus.SUCCEEDED;
    });
};
const intakeReducers = {
  setSegmentStatus(
    state,
    action: PayloadAction<{
      intakeId: number;
      segmentId: number;
      status: DocumentStatus;
      details: SegmentStatusUpdateEvent["processingDetails"];
      timestamp: string;
    }>
  ) {
    const { intakeId, segmentId, status, details, timestamp } = action.payload;
    const foundIntake = state.intakes.find((i) => i.id === intakeId);

    if (foundIntake) {
      const foundSegment = foundIntake.documentSegments.find((s) => s.id === segmentId);
      if (foundSegment) {
        foundSegment.status = status;
        if (status === DocumentStatus.PendingReview) {
          foundSegment.processedAt = timestamp;
        }
        if (details) {
          foundSegment.processingDetails = details;
        }
      }
    }
  },
  setIntakeSegmentCount(state, action: PayloadAction<{ intakeId: number; segmentCount: number }>) {
    const { intakeId, segmentCount } = action.payload;
    const found = state.intakes.find((i) => i.id === intakeId);

    if (found) {
      found.segmentMetrics.segmentNumber = segmentCount;
    }
  },
  setIntakeStatus(
    state,
    action: PayloadAction<{ intakeId: number; status: DocumentStatus; segmentMetrics?: SegmentMetrics }>
  ) {
    const { intakeId, status, segmentMetrics } = action.payload;
    const found = state.intakes.find((cD) => cD.id === intakeId);

    if (found) {
      found.status = status;
      if (segmentMetrics) {
        found.segmentMetrics = segmentMetrics;
      }
    }
  },
  setAllIntakesReviewed(state) {
    state.intakes.forEach((i) => (i.status = DocumentStatus.Reviewed));
  },
};
const intakeExtraReducers = (builder: ActionReducerMapBuilder<CaseState>): void => {
  builder
    .addCase(updateIntakeCustomProperties.fulfilled, (state, action) => {
      const intake = state.intakes.find((i) => i.id === action.payload.intakeId);
      if (intake !== undefined) {
        const props: KeyValue[] = action.payload.properties.filter((prop) => prop.value !== undefined) as KeyValue[];
        intake.properties = props;
      }
    })
    .addCase(fetchCaseIntakes.pending, (state) => {
      state.status = RequestStatus.PENDING;
    })
    .addCase(fetchCaseIntakes.fulfilled, (state, action) => {
      state.intakes = action.payload;
      state.status = RequestStatus.SUCCEEDED;
    })
    .addCase(fetchCaseIntakes.rejected, (state) => {
      state.status = RequestStatus.FAILED;
    })
    .addCase(fetchIntake.fulfilled, (state, action) => {
      const intake = action.payload.case.document;
      const foundIndex = state.intakes.findIndex((i) => i.id === intake.id);
      if (foundIndex !== -1) {
        state.intakes[foundIndex] = intake;
      } else {
        state.intakes.push(intake);
      }
    })
    .addCase(fetchSegment.fulfilled, (state, action) => {
      const segment = action.payload;
      const intake = state.intakes.find((i) => i.id === segment.intakeId);

      if (intake !== undefined) {
        const segments = intake.documentSegments;
        const foundSegmentIndex = segments.findIndex((ds) => ds.id === segment.id);
        if (foundSegmentIndex === -1) {
          segments.push(segment);
          segments.sort((ds1, ds2) => ds1.partIndex - ds2.partIndex);
        } else {
          segments[foundSegmentIndex] = segment;
        }
      }
    })
    .addCase(deleteIntake.fulfilled, (state, action) => {
      const foundIndex = state.intakes.findIndex((i) => i.id === action.payload.intakeId);

      if (foundIndex > -1) {
        state.intakes.splice(foundIndex, 1);
      }
    })
    .addCase(renameIntake.fulfilled, (state, action) => {
      const { intakeId, newName } = action.payload;
      const found = state.intakes.find((i) => i.id === intakeId);

      if (found) {
        found.name = newName;
      }
    })
    .addCase(reintakeDocument.fulfilled, (state, action) => {
      const { intakeId, reprocessType } = action.payload;
      const found = state.intakes.find((i) => i.id === intakeId);
      if (found) {
        found.status = DocumentStatus.Processing;
        switch (reprocessType) {
          case ReprocessType.ReIntake:
            found.documentSegments = [];
            found.segmentMetrics = {
              segmentNumber: 0,
              segmentsErrored: 0,
              segmentsCompleted: 0,
            };
            break;
          default:
            break;
        }
      }
    });
};
const caseReducers = {
  init: (state, action: PayloadAction<{ case: Case }>) => {
    state.case = action.payload.case;
  },
  setCaseReviewStatus: (state, action: PayloadAction<{ reviewStatus: CaseReviewStatus }>) => {
    state.case.reviewStatus = action.payload.reviewStatus;
  },
  setCaseOverallOutcomeStatus: (state, action: PayloadAction<{ status: OverallOutcomeStatus }>) => {
    state.case.outcomeStatus = action.payload.status;
  },
  setCaseInsightsPending: (state, action: PayloadAction<boolean>) => {
    state.caseInsightsPending = action.payload;
  },
};
const caseExtraReducers = (builder: ActionReducerMapBuilder<CaseState>): void => {
  builder
    .addCase(fetchCaseHome.fulfilled, (state, { payload }) => {
      state.status = RequestStatus.SUCCEEDED;
      state.case = payload.case.data;
      state.calcs = valueCalcs(payload.case.calcs.calcs);
      state.reviews = payload.case.reviews;
      state.intakes = payload.case.documents;
      state.intakesStatus = RequestStatus.SUCCEEDED;
      state.exports = payload.case.caseExports;
      state.exportsStatus = RequestStatus.SUCCEEDED;
      state.collaboratorFirms = payload.case.collaboratorFirms;
      state.sectionValues = payload.case.data.sectionValues;
    })
    .addCase(fetchPossibleChangesFromAutoScripts.fulfilled, (state, { payload }) => {
      state.case.outcomeStatus = payload.case.data.outcomeStatus;
      state.case.clientStatus = payload.case.data.clientStatus;
      state.case.properties = payload.case.data.properties;
      state.case.reviewStatus = payload.case.data.reviewStatus;
      state.case.reviewStatusReason = payload.case.data.reviewStatusReason;
      state.case.summary = payload.case.data.summary;
      state.sectionValues = payload.case.data.sectionValues;
      state.calcs = valueCalcs(payload.case.calcs.calcs);
    })
    .addCase(fetchCaseSectionValues.fulfilled, (state, { payload }) => {
      state.sectionValues = payload;
    })
    .addCase(fetchCase.pending, (state) => {
      state.status = RequestStatus.PENDING;
      state.case = EMPTY_CASE;
    })
    .addCase(fetchCase.fulfilled, (state, action) => {
      state.case = action.payload;
      state.status = RequestStatus.SUCCEEDED;
    })
    .addCase(fetchCase.rejected, (state) => {
      state.status = RequestStatus.FAILED;
    })
    .addCase(fetchCaseSilent.fulfilled, (state, action) => {
      state.case = action.payload;
    })
    .addCase(updateCaseClaimantInfo.fulfilled, (state, action) => {
      if (state.case) {
        const { info, userAssignee, caseProperties } = action.payload;
        state.case.info = info;
        state.case.userAssignee = userAssignee;

        caseProperties.forEach((cp) => {
          const foundIndex = state.case.properties.findIndex((p) => p.key === cp.key);
          const property = state.case.properties[foundIndex];
          if (foundIndex !== -1 && property !== undefined) {
            if (cp.value) {
              property.value = cp.value;
            } else {
              state.case.properties.splice(foundIndex, 1);
            }
          } else if (cp.value) {
            state.case.properties.push({ key: cp.key, value: cp.value });
          }
        });
      }
    })
    .addCase(updateCaseClientStatus.fulfilled, (state, action) => {
      if (state.case) {
        state.case.clientStatus = action.payload;
      }
    })
    .addCase(updateCaseClientCaseId.fulfilled, (state, action) => {
      if (state.case) {
        state.case.info.clientCaseId = action.payload.clientCaseId;
      }
    })
    .addCase(updateCaseSummary.fulfilled, (state, action) => {
      if (state.case) {
        state.case.summary = action.payload;
      }
    })
    .addCase(setDocumentsReviewed.fulfilled, (state, action) => {
      if (state.case) {
        if (action.payload.reviewed) {
          state.case.reviewStatus = CaseReviewStatus.Complete;
          state.case.reviewStatusReason = undefined;
        } else {
          state.case.reviewStatus = CaseReviewStatus.New;
          state.case.reviewStatusReason = action.payload.reviewStatusReason;
        }
      }
    })
    .addCase(updateSectionValueValidation.fulfilled, upsertSectionValue)
    .addCase(updateSectionValueOutcome.fulfilled, upsertSectionValue)
    .addCase(updateSectionValueOutcomeAssignee.fulfilled, (state, action) => {
      const { sectionValues } = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.outcomeAssignee = sectionValue.outcomeAssignee;
        }
      });
    })
    .addCase(updateSectionValueValidationAssignee.fulfilled, (state, action) => {
      const { sectionValues } = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.validationAssignee = sectionValue.validationAssignee;
        }
      });
    })
    .addCase(updateSectionValuesReviewState.fulfilled, (state, action) => {
      const sectionValues = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.reviewState = sectionValue.reviewState;
        }
      });
    })
    .addCase(updateSectionValueOutcomeComment.fulfilled, (state, action) => {
      const sectionValues = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.outcomeComment = sectionValue.outcomeComment;
        }
      });
    })
    .addCase(updateSectionValueValidationComment.fulfilled, (state, action) => {
      const sectionValues = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.validationComment = sectionValue.validationComment;
        }
      });
    })
    .addCase(updateSectionValueOutcomeStatus.fulfilled, (state, action) => {
      const sectionValues = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.outcomeStatus = sectionValue.outcomeStatus;
        }
      });
    })
    .addCase(updateSectionValueValidationStatus.fulfilled, (state, action) => {
      const sectionValues = action.payload;
      sectionValues.forEach((sectionValue) => {
        const foundSectionValue = state.sectionValues.find((sv) => sv.sectionId === sectionValue.sectionId);

        if (foundSectionValue !== undefined) {
          foundSectionValue.validationStatus = sectionValue.validationStatus;
        }
      });
    })
    .addCase(setCollaboratorFirms.fulfilled, (state, action) => {
      state.collaboratorFirms = action.payload.firms;
    })
    .addCase(setPlaintiffFirm.fulfilled, (state, action) => {
      state.case.plaintiffFirm = action.payload;
      state.collaboratorFirms = state.collaboratorFirms.filter((firm) => firm.id !== action.payload?.id);
    });
};
const caseSlice = createSlice({
  name: "case",
  initialState,
  reducers: {
    ...intakeReducers,
    ...caseReducers,
    ...exportReducers,
    resetCase: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    intakeExtraReducers(builder);
    caseExtraReducers(builder);
    exportExtraReducers(builder);
    reviewExtraReducers(builder);

    builder.addCase(USER_LOGOUT, () => ({ ...initialState }));
  },
});

export const handleSegmentStatusUpdate = createAsyncThunk<SegmentStatusUpdateEvent, SegmentStatusUpdateEvent>(
  "case/intakeSegmentStatusUpdate",
  (event, { dispatch, getState }) => {
    const state = getState() as RootState;
    const intake = state.case.intakes.find((i) => i.id === event.documentIntakeId);
    const segment = intake?.documentSegments.find((ds) => ds.id === event.segmentId);
    if (!segment) {
      return dispatch(
        fetchSegment({
          caseId: event.caseId,
          segmentId: event.segmentId,
          processingDetails: event.processingDetails,
        })
      )
        .unwrap()
        .then(() => event);
    } else {
      dispatch(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        setSegmentStatus({
          intakeId: event.documentIntakeId,
          segmentId: event.segmentId,
          status: event.status,
          details: event.processingDetails,
          timestamp: event.timestamp,
        })
      );
      return Promise.resolve(event);
    }
  }
);

export default caseSlice;
export const {
  resetCase,
  setCaseReviewStatus,
  setCaseOverallOutcomeStatus,
  setCaseInsightsPending,
  setSegmentStatus,
  setIntakeSegmentCount,
  setAllIntakesReviewed,
  setIntakeStatus,
  deselectExportType,
  selectExportType,
  updateExport,
} = caseSlice.actions;
